import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Table,
    Spinner,
    Badge,
    Button
} from 'reactstrap';
import {
    TbUsers,
    TbArrowUp,
    TbArrowDown,
    TbMapPin,
    TbRefresh,
    TbStack2,
    TbFilter,
    TbUser,
    TbClock,
    TbCheck,
    TbX,
    TbPhone,
    TbMessage,
    TbNavigation
} from 'react-icons/tb';
import { motion, AnimatePresence } from 'framer-motion';
import { getAgent } from '../../../helpers/backend';
import './AgentsDashboard.css';

// React Leaflet imports
import { MapContainer, TileLayer, Marker, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix para iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Configuración de capas de mapa
const MAP_LAYERS = [
    {
        id: 'light',
        name: 'Modo Claro',
        description: 'Mapa claro ideal para visualización diurna',
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        className: 'preview-light'
    },
    {
        id: 'dark',
        name: 'Modo Oscuro',
        description: 'Mapa oscuro para reducir fatiga visual',
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
        className: 'preview-dark'
    },
    {
        id: 'streets',
        name: 'Calles',
        description: 'Enfoque en calles y vías principales',
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        className: 'preview-streets'
    },
    {
        id: 'satellite',
        name: 'Satélite',
        description: 'Imágenes satelitales de alta resolución',
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
        className: 'preview-satellite'
    }
];

// Estados de agente
const AGENT_STATUS = {
    ACTIVE: { label: 'Activo', color: '#10b981', icon: '🟢' },
    BUSY: { label: 'Ocupado', color: '#f59e0b', icon: '🟡' },
    OFFLINE: { label: 'Offline', color: '#64748b', icon: '⚫' }
};

// Componente de marcador personalizado
const AgentMarker = ({ agent, onClick }) => {
    // Determinar estado del agente
    const getAgentStatus = (agent) => {
        if (!agent.is_online) return AGENT_STATUS.OFFLINE;
        if (agent.tasks_active > 3) return AGENT_STATUS.BUSY;
        return AGENT_STATUS.ACTIVE;
    };

    const status = getAgentStatus(agent);

    // Status string for CSS class
    const statusKey = status === AGENT_STATUS.OFFLINE ? 'offline' :
        status === AGENT_STATUS.BUSY ? 'busy' : 'active';

    const position = [parseFloat(agent.latitud), parseFloat(agent.longitud)];

    // Generar iniciales
    const initials = agent.full_name
        ? agent.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
        : agent.username?.charAt(0).toUpperCase() || 'A';

    // Generar color único basado en ID
    const getColorFromId = (id) => {
        const colors = [
            '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
            '#ef4444', '#06b6d4', '#84cc16', '#f97316'
        ];
        return colors[id % colors.length];
    };

    const markerColor = getColorFromId(agent.id || 0);

    // Create custom icon using L.divIcon
    const customIcon = L.divIcon({
        className: 'custom-leaflet-icon',
        html: `
            <div class="agent-marker">
                <div class="marker-avatar" style="background-color: ${markerColor}">
                   ${initials}
                </div>
                <div class="marker-pulse" style="background-color: ${markerColor}"></div>
                <div class="marker-status-indicator status-${statusKey}"></div>
            </div>
        `,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
        popupAnchor: [0, -22]
    });

    return (
        <>
            {/* Anillo de pulso extra para usuarios activos */}
            {status === AGENT_STATUS.ACTIVE && (
                <CircleMarker
                    center={position}
                    radius={15}
                    pathOptions={{
                        fillColor: markerColor,
                        color: markerColor,
                        fillOpacity: 0.1,
                        weight: 1
                    }}
                />
            )}

            {/* Marcador principal */}
            <Marker
                position={position}
                icon={customIcon}
                eventHandlers={{
                    click: () => onClick(agent)
                }}
            >
                <Popup>
                    <AgentPopupCard agent={agent} status={status} color={markerColor} />
                </Popup>
            </Marker>
        </>
    );
};

// Componente de popup de agente
const AgentPopupCard = ({ agent, status, color }) => {
    const metrics = [
        { label: 'Asignados', value: agent.total_asignados || 0, icon: <TbStack2 size={16} className="text-primary opacity-75" /> },
        { label: 'Finalizados', value: agent.total_finalizados || 0, icon: <TbCheck size={16} className="text-success opacity-75" /> },
        { label: 'En Proceso', value: agent.en_proceso || 0, icon: <TbClock size={16} className="text-warning opacity-75" /> },
        { label: 'Pendientes', value: agent.pendientes || 0, icon: <TbUser size={16} className="text-secondary opacity-75" /> }
    ];

    return (
        <div className="agent-popup-card modern-popup">
            <div className="agent-popup-header-modern">
                <div className="d-flex align-items-center justify-content-between w-100">
                    <div className="d-flex align-items-center gap-3">
                         <div
                            className="agent-popup-avatar-modern"
                            style={{ backgroundColor: color }}
                        >
                            {agent.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <div className="agent-popup-info-modern">
                            <h4 className="text-dark mb-1">{agent.full_name || agent.username}</h4>
                            <div className="d-flex align-items-center gap-2">
                                <span className={`status-badge ${status === AGENT_STATUS.ACTIVE ? 'status-active-badge' : status === AGENT_STATUS.BUSY ? 'status-busy-badge' : 'status-offline-badge'}`}>
                                    {status.label}
                                </span>
                                <span className="text-muted small">•</span>
                                <span className="text-muted small">{agent.contactType || 'Agente de Campo'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="agent-popup-body pt-2">
                <div className="agent-metrics-grid-modern">
                    {metrics.map((metric, index) => (
                        <div key={index} className="metric-item-modern">
                            <div className="d-flex justify-content-between align-items-start mb-1">
                                <span className="metric-label-modern">{metric.label}</span>
                                {metric.icon}
                            </div>
                            <p className="metric-value-modern">{metric.value}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Componente de selector de capas
const LayerSelector = ({ isOpen, onClose, currentLayer, onSelectLayer }) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="layer-selector-overlay"
                >
                    <div className="layer-selector-header">
                        <h6 className="layer-selector-title">Estilo del Mapa</h6>
                        <button className="layer-close-btn" onClick={onClose}>
                            <TbX size={20} />
                        </button>
                    </div>

                    <div className="layer-options-grid">
                        {MAP_LAYERS.map(layer => (
                            <div
                                key={layer.id}
                                className={`layer-option-card ${currentLayer === layer.id ? 'active' : ''}`}
                                onClick={() => {
                                    onSelectLayer(layer.id);
                                    onClose();
                                }}
                            >
                                <div className="layer-preview-container">
                                    <div className={`layer-preview ${layer.className}`} />
                                </div>
                                <div className="layer-info">
                                    <p className="layer-name">{layer.name}</p>
                                    <p className="layer-description">{layer.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Componente de filtros
const AgentFilters = ({ filters, onFilterChange, onClearFilters }) => {
    const statusOptions = [
        { id: 'active', label: 'Activos', icon: '🟢' },
        { id: 'busy', label: 'Ocupados', icon: '🟡' },
        { id: 'offline', label: 'Offline', icon: '⚫' }
    ];

    const typeOptions = [
        { id: 'field', label: 'Campo' },
        { id: 'support', label: 'Soporte' },
        { id: 'supervisor', label: 'Supervisor' }
    ];

    return (
        <div className="agent-filters-panel">
            <div className="filter-header">
                <h6 className="filter-title">Filtros</h6>
                <button className="filter-clear" onClick={onClearFilters}>
                    Limpiar
                </button>
            </div>

            <div className="filter-group">
                <p className="filter-group-title">Estado</p>
                <div className="filter-options">
                    {statusOptions.map(option => (
                        <button
                            key={option.id}
                            className={`filter-chip ${filters.status.includes(option.id) ? 'active' : ''}`}
                            onClick={() => onFilterChange('status', option.id)}
                        >
                            {option.icon} {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="filter-group">
                <p className="filter-group-title">Tipo de Agente</p>
                <div className="filter-options">
                    {typeOptions.map(option => (
                        <button
                            key={option.id}
                            className={`filter-chip ${filters.type.includes(option.id) ? 'active' : ''}`}
                            onClick={() => onFilterChange('type', option.id)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Componente principal
const AgentsDashboard = ({ data }) => {
    const [agentsList, setAgentsList] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [currentLayer, setCurrentLayer] = useState('light');
    const [showLayerSelector, setShowLayerSelector] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [filters, setFilters] = useState({
        status: [],
        type: []
    });

    const selectedLayer = useMemo(() =>
        MAP_LAYERS.find(layer => layer.id === currentLayer) || MAP_LAYERS[0],
        [currentLayer]
    );

    const fetchAgents = useCallback(async (isAutoUpdate = false) => {
        try {
            if (!isAutoUpdate) setLoadingAgents(true);
            const response = await getAgent();
            console.log('Agents Response:', response);
            const agents = response?.data || response || [];

            // Agregar datos de ejemplo para demostración
            const enhancedAgents = agents.map((agent, index) => ({
                ...agent,
                is_online: Math.random() > 0.2,
                tasks_active: Math.floor(Math.random() * 6),
                last_seen: new Date(Date.now() - Math.random() * 3600000).toISOString()
            }));

            setAgentsList(enhancedAgents);
            setLastUpdated(new Date());
        } catch (error) {
            console.error("Error fetching agents:", error);
        } finally {
            setLoadingAgents(false);
        }
    }, []);

    useEffect(() => {
        fetchAgents();
        const intervalId = setInterval(() => fetchAgents(true), 15000);
        return () => clearInterval(intervalId);
    }, [fetchAgents]);

    // Filtrar agentes
    const filteredAgents = useMemo(() => {
        return agentsList.filter(agent => {
            // Filtrar por estado
            if (filters.status.length > 0) {
                const status = getAgentStatus(agent);
                const statusKey = status === AGENT_STATUS.ACTIVE ? 'active' :
                    status === AGENT_STATUS.BUSY ? 'busy' : 'offline';

                if (!filters.status.includes(statusKey)) return false;
            }

            // Filtrar por tipo (ejemplo básico)
            if (filters.type.length > 0) {
                const agentType = agent.contactType?.toLowerCase() || 'field';
                const typeKey = agentType.includes('soporte') ? 'support' :
                    agentType.includes('supervisor') ? 'supervisor' : 'field';

                if (!filters.type.includes(typeKey)) return false;
            }

            return true;
        });
    }, [agentsList, filters]);

    const agentsWithLocation = filteredAgents.filter(a => a.latitud && a.longitud);

    // Métricas de productividad
    const productivityAgents = (data?.productividad_agentes || []).filter(a => a.contacto__full_name);
    const avgPorcentaje = productivityAgents.length ?
        (productivityAgents.reduce((acc, a) => acc + (Number(a.porcentaje_finalizados) || 0), 0) / productivityAgents.length) : 0;

    const getAgentStatus = (agent) => {
        if (!agent.is_online) return AGENT_STATUS.OFFLINE;
        if (agent.tasks_active > 3) return AGENT_STATUS.BUSY;
        return AGENT_STATUS.ACTIVE;
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => {
            const currentValues = prev[filterType];
            if (currentValues.includes(value)) {
                return {
                    ...prev,
                    [filterType]: currentValues.filter(v => v !== value)
                };
            } else {
                return {
                    ...prev,
                    [filterType]: [...currentValues, value]
                };
            }
        });
    };

    const handleClearFilters = () => {
        setFilters({ status: [], type: [] });
    };

    const center = [4.6097, -74.0817]; // Bogotá

    if (loadingAgents && agentsList.length === 0) {
        return (
            <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: '400px' }}>
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Cargando datos de agentes...</p>
            </div>
        );
    }

    return (
        <div className="agents-dashboard fade-in">
            <Row className="mb-4">
                <Col xs={12}>
                    <Card className="border-0 shadow-lg overflow-hidden" style={{ borderRadius: '12px' }}>
                        <CardBody className="p-0">
                            {/* Header flotante */}
                            <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <div className="p-2 rounded-circle bg-light me-3 text-primary">
                                        <TbMapPin size={24} />
                                    </div>
                                    <div>
                                        <h5 className="mb-0 fw-bold text-dark">Monitor de Operaciones</h5>
                                        <div className="d-flex align-items-center text-muted small mt-1">
                                            <span className="live-indicator me-3">
                                                <span className="live-dot"></span> En Vivo
                                            </span>
                                            <span className="me-3">•</span>
                                            <span className="me-3">
                                                {agentsWithLocation.length} Agentes Activos
                                            </span>
                                            <span>•</span>
                                            <span className="ms-3">
                                                Actualizado: {lastUpdated.toLocaleTimeString()}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="d-flex gap-2">
                                    <Button
                                        color="primary"
                                        size="sm"
                                        className="rounded-pill d-flex align-items-center"
                                        onClick={() => fetchAgents(false)}
                                    >
                                        <TbRefresh size={18} className="me-2" />
                                        Actualizar
                                    </Button>
                                </div>
                            </div>

                            {/* MAPA CONTAINER */}
                            <div className="position-relative" style={{ height: '550px', backgroundColor: '#f1f5f9' }}>
                                {/* Controles del mapa */}
                                <div className="map-controls-panel">
                                    <button
                                        className={`map-control-button ${showFilters ? 'active' : ''}`}
                                        onClick={() => setShowFilters(!showFilters)}
                                        title="Filtros"
                                    >
                                        <TbFilter size={20} />
                                    </button>
                                    <button
                                        className={`map-control-button ${showLayerSelector ? 'active' : ''}`}
                                        onClick={() => setShowLayerSelector(!showLayerSelector)}
                                        title="Capas del mapa"
                                    >
                                        <TbStack2 size={20} />
                                    </button>
                                    <button
                                        className="map-control-button"
                                        onClick={() => {
                                            // Centrar mapa en Bogotá
                                            // Esta funcionalidad requeriría una referencia al mapa
                                        }}
                                        title="Centrar mapa"
                                    >
                                        <TbNavigation size={20} />
                                    </button>
                                </div>

                                {/* Selector de capas */}
                                <LayerSelector
                                    isOpen={showLayerSelector}
                                    onClose={() => setShowLayerSelector(false)}
                                    currentLayer={currentLayer}
                                    onSelectLayer={setCurrentLayer}
                                />

                                {/* Panel de filtros */}
                                {showFilters && (
                                    <AgentFilters
                                        filters={filters}
                                        onFilterChange={handleFilterChange}
                                        onClearFilters={handleClearFilters}
                                    />
                                )}

                                {/* Mapa Leaflet */}
                                <MapContainer
                                    center={center}
                                    zoom={12}
                                    style={{ width: '100%', height: '100%' }}
                                    className="modern-map"
                                    scrollWheelZoom={true}
                                >
                                    <TileLayer
                                        url={selectedLayer.url}
                                        attribution={selectedLayer.attribution}
                                    />

                                    {/* Marcadores de Agentes */}
                                    {agentsWithLocation.map((agent) => (
                                        <AgentMarker
                                            key={agent.id || agent.username}
                                            agent={agent}
                                            onClick={setSelectedAgent}
                                        />
                                    ))}
                                </MapContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Tabla de Productividad (Mejorada) */}
            <Row>
                <Col xs={12}>
                    <Card className="border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                                    <TbUsers className="me-2" />
                                    Métricas de Rendimiento
                                </h5>
                                <Badge color="light" className="text-dark">
                                    Promedio: {avgPorcentaje.toFixed(1)}%
                                </Badge>
                            </div>

                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light text-muted">
                                        <tr>
                                            <th className="fw-semibold ps-3">Agente</th>
                                            <th className="fw-semibold text-center">Estado</th>
                                            <th className="fw-semibold text-center">Asignados</th>
                                            <th className="fw-semibold text-center">Finalizados</th>
                                            <th className="fw-semibold text-center">En Proceso</th>
                                            <th className="fw-semibold text-center">Pendientes</th>
                                            <th className="fw-semibold text-center">Efectividad</th>
                                            <th className="fw-semibold text-center">Tendencia</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productivityAgents.map((agente, index) => {
                                            const growth = (Number(agente.porcentaje_finalizados) || 0) - avgPorcentaje;
                                            const isPositive = growth >= 0;
                                            const agentStatus = getAgentStatus(
                                                agentsList.find(a => a.full_name === agente.contacto__full_name) || {}
                                            );

                                            return (
                                                <motion.tr
                                                    key={index}
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: index * 0.05 }}
                                                    className="border-bottom"
                                                >
                                                    <td className="ps-3">
                                                        <div className="d-flex align-items-center">
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold shadow-sm"
                                                                style={{
                                                                    width: '36px',
                                                                    height: '36px',
                                                                    backgroundColor: `hsl(${index * 137.5}, 70%, 50%)`,
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                {agente.contacto__full_name?.charAt(0)?.toUpperCase()}
                                                            </div>
                                                            <div>
                                                                <div className="fw-semibold text-dark">
                                                                    {agente.contacto__full_name}
                                                                </div>
                                                                <small className="text-muted">
                                                                    {agentsList.find(a => a.full_name === agente.contacto__full_name)?.contactType || 'Agente'}
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge
                                                            color="light"
                                                            className="border"
                                                            style={{
                                                                color: agentStatus.color,
                                                                borderColor: agentStatus.color + '30',
                                                                backgroundColor: agentStatus.color + '10'
                                                            }}
                                                        >
                                                            {agentStatus.label}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center fw-bold">{agente.total_asignados}</td>
                                                    <td className="text-center text-success fw-bold">{agente.total_finalizados}</td>
                                                    <td className="text-center text-primary fw-bold">{agente.en_proceso}</td>
                                                    <td className="text-center text-warning fw-bold">{agente.pendientes}</td>
                                                    <td className="text-center">
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            <div className="progress w-100" style={{ height: '6px', maxWidth: '80px', marginRight: '8px' }}>
                                                                <div
                                                                    className="progress-bar"
                                                                    style={{
                                                                        width: `${Math.min(100, Number(agente.porcentaje_finalizados))}%`,
                                                                        backgroundColor: Number(agente.porcentaje_finalizados) >= 90 ? '#10b981' :
                                                                            Number(agente.porcentaje_finalizados) >= 50 ? '#3b82f6' : '#ef4444'
                                                                    }}
                                                                ></div>
                                                            </div>
                                                            <span className="small fw-bold">
                                                                {(Number(agente.porcentaje_finalizados) || 0).toFixed(0)}%
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className={`badge border ${isPositive ?
                                                            'bg-success bg-opacity-10 text-success border-success' :
                                                            'bg-danger bg-opacity-10 text-danger border-danger'}`}
                                                        >
                                                            {isPositive ? <TbArrowUp /> : <TbArrowDown />}
                                                            {Math.abs(growth).toFixed(1)}%
                                                        </span>
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

// Función auxiliar para obtener estado del agente
const getAgentStatus = (agent) => {
    if (!agent.is_online) return AGENT_STATUS.OFFLINE;
    if (agent.tasks_active > 3) return AGENT_STATUS.BUSY;
    return AGENT_STATUS.ACTIVE;
};

export default AgentsDashboard;